import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ContentTypeBadge,
  CopyField,
  EmptyState,
  Page,
  StatusBadge,
} from "../../../components/ui";
import {
  DetailHeader,
  DetailSkeleton,
  useStoredDetail,
} from "../../../components/detail";
import {
  getChannel,
  updateChannel,
  updateChannelThumbnail,
} from "../../../services/channels";
import { getChannelGC } from "../../../store/actions/uploadMediaAction";
import ChannelForm, { channelFormValues, channelPayload } from "./ChannelForm";

const UpdateChannel = () => {
  const { channel_id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { item: channel, loading } = useStoredDetail({
    load: () => getChannel(channel_id),
    setInfo: getChannelGC,
    storeKey: "generalContentObjChannel",
    pagePath: `/dashboard/update-channel/${channel_id}`,
  });
  const [formKey, setFormKey] = useState(0);

  const handleUpdate = async (values) => {
    const response = await updateChannel(
      channelPayload(values, { withBanner: false }),
      channel._id
    );
    if (!response) {
      toast.error(t("Error In General Content"));
      return;
    }
    if (values.imageBase64) {
      await updateChannelThumbnail(
        { imageBase64: values.imageBase64 },
        channel.thumbnail?._id
      );
    }
    dispatch(getChannelGC(await getChannel(channel._id)));
    setFormKey((key) => key + 1);
    toast.success(t("Channel Info Updated Successfully!"));
  };

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }
  if (!channel) {
    return (
      <Page>
        <DetailHeader title={t("Update Channel")} />
        <EmptyState icon="ri-broadcast-line">
          {t("No data found for the provided ID!")}
        </EmptyState>
      </Page>
    );
  }

  return (
    <Page>
      <DetailHeader
        title={channel.title || t("Update Channel")}
        badges={
          <>
            <StatusBadge active={Boolean(channel.status)} />
            <ContentTypeBadge type={channel.content_type} />
          </>
        }
      />

      {channel.channel_type === "encoder" && (
        <section className="ui-card content-side-card channel-stream">
          <h2 className="content-side-title">{t("Streaming")}</h2>
          <p className="ui-hint">
            {t("Enter these values in your encoder (OBS, vMix…) to go live.")}
          </p>
          <div className="channel-stream-fields">
            <CopyField
              id="channel-stream-url"
              label={t("Stream URL")}
              value={process.env.REACT_APP_STREAM_URL}
            />
            <CopyField
              id="channel-stream-key"
              label={t("Stream Key")}
              value={channel.stream_key}
            />
          </div>
        </section>
      )}

      <h2 className="content-section-title">{t("General information")}</h2>
      <ChannelForm
        key={`${channel._id}-${formKey}`}
        initialValues={channelFormValues(channel)}
        withBanner={false}
        onSubmit={handleUpdate}
        submitLabel={t("Update")}
        cancelPath="/dashboard/channel-list"
      />
    </Page>
  );
};

export default React.memo(UpdateChannel);
